import { useCallback, useEffect, useState } from 'react';
import { get } from '../api';

export type ProgramMandate = {
    strategy: string;
    instruments: string[];
};

export type ProgramSummary = {
    id: number;
    key: string;
    title: string;
    description: string;
    mandates: ProgramMandate[];
    instruments: string[];
    instrument_count: number;
    is_active: boolean;
    update_time: string;
    run_count: number;
    last_run_at: string | null;
    latest_sample: string | null;
    latest_sum_pct: number | null;
    latest_sharpe: number | null;
    latest_passed: boolean | null;
    latest_num_windows: number | null;
    latest_num_trades: number | null;
    latest_run_at: string | null;
};

export type RunSummary = {
    id: number;
    subject_kind: 'program' | 'strategy';
    subject_key: string;
    program_key: string | null;
    sample: string;
    window_days: number;
    num_windows: number;
    windows_ok: number;
    windows_positive: number | null;
    period_start: string | null;
    period_end: string | null;
    constituent_count: number;
    initial_balance: number | null;
    leverage: number | null;
    passed: boolean | null;
    verdict_reason: string | null;
    cumulative_return: number | null;
    mean_window_return: number | null;
    worst_window_return: number | null;
    sharpe: number | null;
    num_trades: number | null;
    gross_return: number | null;
    cost_drag: number | null;
    funding_return: number | null;
    elapsed_secs: number | null;
    source_file: string | null;
    code_version_id: number | null;
    insert_time: string;
};

export type CombinedRecord = {
    samples: string[];
    num_windows: number;
    cumulative_return: number;
    windows_positive: number;
    worst_window_return: number;
    sharpe: number | null;
    num_trades: number;
};

export type ProgramDetail = Omit<ProgramSummary, 'run_count' | 'last_run_at' | 'latest_sample' | 'latest_sum_pct' | 'latest_sharpe' | 'latest_passed' | 'latest_num_windows' | 'latest_num_trades' | 'latest_run_at'> & {
    insert_time: string;
    runs: RunSummary[];
    combined: CombinedRecord | null;
};

export type RunWindow = {
    id: number;
    window_end: string;
    from_time: string | null;
    to_time: string | null;
    ok: boolean;
    error: string | null;
    return_pct: number | null;
    net_pnl: number | null;
    gross_pnl: number | null;
    funding_pnl: number | null;
    transaction_costs: number | null;
    num_trades: number | null;
    num_long: number | null;
    num_short: number | null;
    win_rate_pct: number | null;
    profit_factor: number | null;
    avg_hold_min: number | null;
    max_drawdown_pct: number | null;
    initial_balance: number | null;
};

export type RunInstrument = {
    instrument: string;
    pnl: number;
    windows: number;
    windows_positive: number;
};

export type RunDetail = RunSummary & {
    code_subject_hash: string | null;
    code_git_commit: string | null;
    code_git_dirty: boolean | null;
    constituents: string[];
    overrides: Record<string, unknown>;
    windows: RunWindow[];
    instruments: RunInstrument[];
};

function useResource<T>(path: string | null, fallback: T) {
    const [data, setData] = useState<T>(fallback);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        if (path === null) return;
        setIsLoading(true);
        setError(null);
        try {
            const response = await get(path);
            if (!response.ok) throw new Error(`Request failed (${response.status})`);
            setData(await response.json());
        } catch (requestError) {
            setError(requestError instanceof Error ? requestError.message : 'Request failed');
        } finally {
            setIsLoading(false);
        }
    }, [path]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    return { data, isLoading, error, refresh };
}

export function usePrograms() {
    const { data, isLoading, error, refresh } = useResource<ProgramSummary[]>('/research/programs', []);
    return { programs: data, isLoading, error, refresh };
}

export function useProgramDetail(key: string | undefined) {
    const { data, isLoading, error, refresh } = useResource<ProgramDetail | null>(key ? `/research/programs/${key}` : null, null);
    return { program: data, isLoading, error, refresh };
}

export function useRunDetail(runId: string | undefined) {
    const { data, isLoading, error, refresh } = useResource<RunDetail | null>(runId ? `/research/runs/${runId}` : null, null);
    return { run: data, isLoading, error, refresh };
}
